export interface AgreementSection {
  title: string;
  content: string | string[];
  subsections?: AgreementSection[];
}

export interface AgreementContent {
  title: string;
  version: string;
  lastUpdated: string;
  introduction: string;
  sections: AgreementSection[];
  contactInfo: {
    email: string;
  };
}

export const AGREEMENT_CONTENT: AgreementContent = {
  title: "Terms & Conditions for Shaku",
  version: "v1.0",
  lastUpdated: "12/10/2025",
  introduction: "These Terms apply to users who sign in through Shaku's embedded SDK on our demo site or on Partner Sites. By signing in, you agree to these Terms.",
  sections: [
    {
      title: "Service Description",
      content: [
        "The Shaku SDK provides:",
        "• Secure user authentication",
        "• Access to measurement results (if you have an account)",
        "• Size recommendations on Partner Sites that use Shaku",
        "• Basic profile management",
        "",
        "Shaku does not own or sell clothing.",
      ],
    },
    {
      title: "User Responsibilities",
      content: [
        "By using our SDK, you agree:",
        "• To provide accurate information",
        "• Not to share your account details with others",
        "• Not to attempt unauthorized access, reverse engineering, or misuse of the SDK",
        "",
        "You are responsible for any activity under your account.",
      ],
    },
    {
      title: "Size Recommendation Disclaimer",
      content: [
        "Shaku uses AI-based estimations to suggest clothing sizes. However:",
        "• Size charts vary between brands",
        "• Fit preference is personal",
        "• Retail product data may change",
        "",
        "Therefore, Shaku is not responsible for incorrect size recommendations or return costs.",
      ],
    },
    {
      title: "Affiliate Products & External Websites",
      content: [
        "Partner Sites may show clothing powered by Shaku results. You acknowledge that:",
        "• Shaku does not manufacture or sell these clothes",
        "• Product details belong to the retailer",
        "• Shaku is not liable for purchases made outside our platform",
        "• Affiliate relationships may result in Shaku receiving commissions",
      ],
    },
    {
      title: "Account Security",
      content: [
        "Shaku:",
        "• Stores passwords only in hashed (non-readable) form",
        "• Uses secure authentication practices",
        "",
        "Users must:",
        "• Maintain confidentiality of login credentials",
        "• Notify us if unauthorized access is suspected",
      ],
    },
    {
      title: "Data Processing & Privacy",
      content: [
        "Your data is processed in accordance with our Privacy Policy, which complies with:",
        "• PIPEDA (Canada)",
        "• GDPR (EU)",
        "• Applicable provincial privacy laws",
        "",
        "Your data may be processed to provide accurate measurement and fitting recommendations.",
      ],
    },
    {
      title: "Termination",
      content: ["We may suspend or terminate your account if:", "• You violate these Terms", "• Fraud or misuse is detected", "• Required by law", "", "You may delete your account at any time."],
    },
    {
      title: "Liability Limitations",
      content: ["To the maximum extent permitted by law:", "• Shaku is not liable for indirect or consequential damages", "• Shaku is not liable for product issues from third-party retailers"],
    },
    {
      title: "Governing Law",
      content: "These Terms are governed by the laws of Ontario, Canada, and applicable federal laws.",
    },
    {
      title: "Contact",
      content: "For legal or account questions:",
    },
  ],
  contactInfo: {
    email: "support@shaku.tech",
  },
};

export const getAgreementPlainText = (): string => {
  let text = `${AGREEMENT_CONTENT.title} (${AGREEMENT_CONTENT.version})\n`;
  text += `Last Updated: ${AGREEMENT_CONTENT.lastUpdated}\n\n`;
  text += `${AGREEMENT_CONTENT.introduction}\n\n`;

  AGREEMENT_CONTENT.sections.forEach((section) => {
    text += `${section.title}\n`;
    if (Array.isArray(section.content)) {
      section.content.forEach((line) => {
        text += `${line}\n`;
      });
    } else if (section.content) {
      text += `${section.content}\n`;
    }

    if (section.subsections) {
      section.subsections.forEach((sub) => {
        text += `\n${sub.title}\n`;
        if (Array.isArray(sub.content)) {
          sub.content.forEach((line) => {
            text += `${line}\n`;
          });
        } else {
          text += `${sub.content}\n`;
        }
      });
    }
    text += "\n";
  });

  text += `\nIf you have questions, contact us at: ${AGREEMENT_CONTENT.contactInfo.email}`;

  return text;
};
